import type { PropsWithChildren, FunctionComponent } from 'react'
import { useEffect } from 'react'
import { useTheme } from '@emotion/react'
import FeatureEducationModal from '../../legacy/components/FeatureHighlights/FeatureEducationModal'
import useFeatureEducation from '../../legacy/components/FeatureHighlights/useFeatureEducation'
import { AppContentContainer } from '../../gin-and-tonic/containers/app-content-container/AppContentContainer'
import ErrorBoundary from '../../legacy/components/ErrorBoundary'
import { type DashRouteComponentProps } from '../../legacy/dash-components/dash-route/utils/dashRoute.types'
import { ContactHeader } from '../../routes/utils/ContactHeader'
import { NoticeBannerHost } from '../../components/notice-banner-host/NoticeBannerHost'
import {
  TdsNavigationBar,
  TOPBAR_HEIGHT,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from '../../gin-and-tonic/containers/nav-v3/TdsNavigationBar'
import { TdsSearchButton } from '../../gin-and-tonic/containers/nav-v3/TdsSearchButton'
import { TdsPartnerMenu } from '../../gin-and-tonic/containers/nav-v3/TdsPartnerMenu'
import { useNavHierarchyAdminV3 } from '../../gin-and-tonic/containers/nav-v3/useNavHierarchyAdminDataV3'
import { useV3SearchActionsAdmin } from '../../gin-and-tonic/containers/nav-v3/useV3SearchActions'
import { useSidebarCollapsedPersistence } from '../../gin-and-tonic/containers/nav-v3/useSidebarCollapsedPersistence'
import { useNavContext } from '../../gin-and-tonic/containers/nav/utils/NavContext'
import { useDatadogPrivacy } from '../../gin-and-tonic/containers/datadog-privacy/DatadogPrivacy'
import { AiPageLayout } from '../../utils/contexts/ai/AiPageLayout'
import { useImpersonationBannerHeight } from '../../gin-and-tonic/containers/impersonation-status-banner/ImpersonationStatusBanner'
import { AdminSectionRoutes } from './AdminSectionRoutes'
import { AdminPageViewTracker } from './components/root/recently-viewed/AdminPageViewTracker'

interface Props extends DashRouteComponentProps {}

/**
 * v3 nav bar wrapper. Owns `useNavHierarchyAdminV3()`, which assembles the
 * 8-L1 admin sidebar (Performance / Catalog / Marketplace / Customers &
 * orders / Connected stores / Fulfillment / Financial integrity / Partner
 * settings) from `domainAdminNavigationEntriesV3` — see useNavHierarchyAdminDataV3.tsx.
 */
const AdminNavBar: FunctionComponent<{
  isCollapsed: boolean
  setIsCollapsed: (next: boolean) => void
}> = ({ isCollapsed, setIsCollapsed }) => {
  const { navMenuHierarchy } = useNavHierarchyAdminV3()
  const searchActions = useV3SearchActionsAdmin()
  const theme = useTheme()
  // Hack: TDS NavigationBar exposes no color/theme prop. Force just the topbar
  // (<header>) dark to match the admin's `systemGrayscale80` styling — gives
  // users the visual cue they're in the admin area without restyling the
  // sidebar. The wrapper must propagate the parent flex column's vertical
  // space (flex: 1 + flexDirection: column); TDS Sidebar relies on flexGrow
  // inside NavigationBar's own Flex.Column, so a content-sized wrapper would
  // collapse the sidebar to zero height.
  return (
    <div
      css={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '& header': {
          backgroundColor: `${theme.colors.systemGrayscale80} !important`,
        },
        // TDS NavigationBar.Button (via ids-core ButtonCore) keys hover/press
        // off `data-hovered`/`data-pressed` attributes; default backgrounds
        // are `brandPrimaryRegular`/`brandPrimaryExtraDark` (green). Swap to
        // grays so admin button feedback matches the dark topbar.
        '& header button[data-hovered="true"]': {
          backgroundColor: `${theme.colors.systemGrayscale60} !important`,
        },
        '& header button[data-pressed="true"]': {
          backgroundColor: `${theme.colors.systemGrayscale70} !important`,
        },
      }}
    >
      <TdsNavigationBar
        navMenuHierarchy={navMenuHierarchy}
        isCollapsed={isCollapsed}
        onCollapsedChange={setIsCollapsed}
        homeRoute="app-admin"
        topBarRight={
          <>
            <TdsSearchButton actions={searchActions} />
            <TdsPartnerMenu />
          </>
        }
      />
    </div>
  )
}

export const AdminSection: FunctionComponent<PropsWithChildren<Props>> = ({
  location: { pathname },
}) => {
  const { isNavVisible } = useNavContext()

  const { isOpen, onOpen, onClose, onClick, feature } = useFeatureEducation(pathname)

  useEffect(() => {
    if (isOpen) {
      onOpen()
    }
  }, [isOpen, onOpen, pathname])

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useSidebarCollapsedPersistence()
  const currentSidebarWidth = isSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH
  const bannerHeight = useImpersonationBannerHeight()

  // Since admin has more privileged or sensitive tools, we mask user input by default
  useDatadogPrivacy('mask-user-input')

  return (
    <>
      <AdminPageViewTracker />
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          height: `calc(100vh - ${bannerHeight}px)`,
          width: '100vw',
          position: 'relative',
        }}
      >
        {isNavVisible && (
          <AdminNavBar isCollapsed={isSidebarCollapsed} setIsCollapsed={setIsSidebarCollapsed} />
        )}
        <div
          css={{
            position: 'absolute',
            top: isNavVisible ? TOPBAR_HEIGHT : 0,
            left: isNavVisible ? currentSidebarWidth : 0,
            width: isNavVisible ? `calc(100% - ${currentSidebarWidth}px)` : '100%',
            height: isNavVisible ? `calc(100% - ${TOPBAR_HEIGHT}px)` : '100%',
            display: 'flex',
          }}
        >
          <FeatureEducationModal
            // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
            isOpen={isOpen}
            onClose={onClose}
            onClick={onClick}
            // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
            feature={feature}
          />
          <AiPageLayout>
            <AppContentContainer css={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
              <NoticeBannerHost />
              <ContactHeader />
              <ErrorBoundary pathname={pathname}>
                <AdminSectionRoutes />
              </ErrorBoundary>
            </AppContentContainer>
          </AiPageLayout>
        </div>
      </div>
    </>
  )
}
